import { useState, useMemo } from "react";
import {
  CATEGORY_LABELS,
  type EmailTemplate,
  type TemplateCategory,
} from "../../lib/email-templates.ts";
import {
  deleteCustomTemplate,
  duplicateTemplate,
} from "../../lib/email-storage.ts";
import { toast } from "../Toast.tsx";
import { useConfirm } from "../useConfirm.tsx";

interface TemplateViewerProps {
  templates: EmailTemplate[];
  onTemplatesChanged: () => void;
  onSelectTemplateForSend: (templateId: string) => void;
  onEditTemplate: (template: EmailTemplate) => void;
}

export function TemplateViewer({
  templates,
  onTemplatesChanged,
  onSelectTemplateForSend,
  onEditTemplate,
}: TemplateViewerProps) {
  const [selectedId, setSelectedId] = useState<string>(templates[0]?.id ?? "");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [viewMode, setViewMode] = useState<"preview" | "code">("preview");
  const [deviceWidth, setDeviceWidth] = useState<"desktop" | "mobile">("desktop");
  const { ask, dialog } = useConfirm();

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const matchesCat = categoryFilter === "all" || t.category === categoryFilter;
      const q = search.toLowerCase().trim();
      const matchesSearch =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.subject.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q);
      return matchesCat && matchesSearch;
    });
  }, [templates, categoryFilter, search]);

  const activeTemplate = useMemo(() => {
    return templates.find((t) => t.id === selectedId) ?? filteredTemplates[0] ?? templates[0];
  }, [templates, selectedId, filteredTemplates]);

  function handleDuplicate(template: EmailTemplate) {
    try {
      const copy = duplicateTemplate(template);
      onTemplatesChanged();
      setSelectedId(copy.id);
      toast(`Created copy: "${copy.name}". You can now edit it.`, true);
      onEditTemplate(copy);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to copy template");
    }
  }

  function handleDelete(template: EmailTemplate) {
    if (template.isDefault) return;

    ask({
      title: "Delete custom template?",
      message: (
        <>
          Are you sure you want to delete <strong>{template.name}</strong>? This action cannot be
          undone.
        </>
      ),
      confirmLabel: "Delete template",
      danger: true,
      run: () => {
        try {
          deleteCustomTemplate(template.id);
          onTemplatesChanged();
          toast("Template deleted.", true);
          const next = templates.find((t) => t.id !== template.id);
          if (next) setSelectedId(next.id);
        } catch (err) {
          toast(err instanceof Error ? err.message : "Failed to delete template");
        }
      },
    });
  }

  const categories: { key: string; label: string }[] = [
    { key: "all", label: "All Templates" },
    { key: "onboarding", label: "Onboarding" },
    { key: "support", label: "Support" },
    { key: "feedback", label: "Feedback" },
    { key: "scaling", label: "Scaling" },
    { key: "reengagement", label: "Re-engagement" },
    { key: "custom", label: "Custom" },
  ];

  return (
    <div className="email-template-workspace">
      {/* Sidebar: Template catalog */}
      <div className="email-template-list-pane">
        <div className="email-search-wrap">
          <input
            type="text"
            className="input input-sm"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="email-category-chips">
          {categories.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`pill-btn ${categoryFilter === c.key ? "active" : ""}`}
              onClick={() => setCategoryFilter(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="email-template-cards">
          {filteredTemplates.length === 0 ? (
            <div className="muted p-4 text-center">No templates found.</div>
          ) : (
            filteredTemplates.map((t) => {
              const isSelected = t.id === (activeTemplate?.id ?? "");
              return (
                <div
                  key={t.id}
                  className={`email-tpl-card ${isSelected ? "selected" : ""}`}
                  onClick={() => setSelectedId(t.id)}
                >
                  <div className="email-tpl-card-head">
                    <span className="email-tpl-name">{t.name}</span>
                    {t.isDefault ? (
                      <span className="badge badge-neutral" title="Non-deletable default template">
                        Default
                      </span>
                    ) : (
                      <span className="badge badge-accent">My Template</span>
                    )}
                  </div>
                  <div className="email-tpl-subject mono muted">{t.subject}</div>
                  <div className="email-tpl-meta">
                    <span className="email-cat-tag">
                      {CATEGORY_LABELS[t.category as TemplateCategory] ?? t.category}
                    </span>
                    {t.updatedAt && (
                      <span className="muted text-xs">
                        {new Date(t.updatedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Main pane: Template preview & actions */}
      {activeTemplate ? (
        <div className="email-template-detail-pane">
          <div className="email-preview-header">
            <div>
              <div className="flex items-center gap-2">
                <h2>{activeTemplate.name}</h2>
                {activeTemplate.isDefault ? (
                  <span className="badge badge-neutral">System Default (Immutable)</span>
                ) : (
                  <span className="badge badge-accent">Custom Template</span>
                )}
              </div>
              <p className="hint mt-1">{activeTemplate.description}</p>
            </div>

            <div className="email-header-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => onSelectTemplateForSend(activeTemplate.id)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M22 2L11 13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                Send with this template
              </button>

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                title="Create an editable copy of this template"
                onClick={() => handleDuplicate(activeTemplate)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy to new template
              </button>

              {!activeTemplate.isDefault && (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => onEditTemplate(activeTemplate)}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger-ghost btn-sm"
                    onClick={() => handleDelete(activeTemplate)}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="email-meta-strip">
            <div className="email-meta-item">
              <span className="email-meta-label">Subject:</span>
              <span className="email-meta-value mono">{activeTemplate.subject}</span>
            </div>
            <div className="email-meta-item">
              <span className="email-meta-label">Sender:</span>
              <span className="email-meta-value mono">CurrencyCore &lt;mail@currencycore.dev&gt; (or mail@currency-core.com)</span>
            </div>
            <div className="email-meta-item">
              <span className="email-meta-label">Replies route to:</span>
              <span className="email-meta-value mono">mail@currencycore.dev (your personal inbox)</span>
            </div>
          </div>

          {/* Preview toolbar */}
          <div className="email-preview-bar">
            <div className="email-toggle-group">
              <button
                type="button"
                className={`btn btn-xs ${viewMode === "preview" ? "btn-secondary" : "btn-ghost"}`}
                onClick={() => setViewMode("preview")}
              >
                Rendered Preview
              </button>
              <button
                type="button"
                className={`btn btn-xs ${viewMode === "code" ? "btn-secondary" : "btn-ghost"}`}
                onClick={() => setViewMode("code")}
              >
                HTML Source
              </button>
            </div>

            {viewMode === "preview" && (
              <div className="email-device-toggle">
                <button
                  type="button"
                  className={`btn btn-xs ${deviceWidth === "desktop" ? "btn-secondary" : "btn-ghost"}`}
                  onClick={() => setDeviceWidth("desktop")}
                >
                  Desktop (580px)
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${deviceWidth === "mobile" ? "btn-secondary" : "btn-ghost"}`}
                  onClick={() => setDeviceWidth("mobile")}
                >
                  Mobile (360px)
                </button>
              </div>
            )}
          </div>

          {/* Live Preview Container */}
          <div className="email-canvas-wrapper">
            {viewMode === "preview" ? (
              <div className={`email-iframe-box ${deviceWidth}`}>
                <iframe
                  title="Email Preview"
                  srcDoc={activeTemplate.html}
                  sandbox="allow-popups allow-same-origin"
                  className="email-iframe"
                />
              </div>
            ) : (
              <div className="email-code-box">
                <pre>
                  <code>{activeTemplate.html}</code>
                </pre>
              </div>
            )}
          </div>

          <div className="email-tpl-variables-footer">
            <span className="email-meta-label">Available Dynamic Variables:</span>
            <span className="var-badge" title="Recipient user display name">{"{{name}}"}</span>
            <span className="var-badge" title="Recipient email address">{"{{email}}"}</span>
            <span className="var-badge" title="Recipient top plan tier">{"{{top_plan}}"}</span>
            <span className="var-badge" title="Number of organizations owned">{"{{org_count}}"}</span>
            <span className="var-badge" title="Account creation date">{"{{signup_date}}"}</span>
          </div>
        </div>
      ) : (
        <div className="email-template-detail-pane empty-pane">
          <p className="muted">Select a template to view details and preview.</p>
        </div>
      )}

      {dialog}
    </div>
  );
}
